// src/components/audit/AuditTable.tsx
// Table component for displaying audit records

import React from 'react';
import { ChevronRight, User, Package, Clock } from 'lucide-react';
import { AuditRecord, getEventTypeDisplay, getActionColor } from '../../types/audit';

interface AuditTableProps {
  records: AuditRecord[];
  loading: boolean;
  onRowClick: (record: AuditRecord) => void;
}

const AuditTable: React.FC<AuditTableProps> = ({ records, loading, onRowClick }) => {
  // Format timestamp for display
  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Format relative time
  const getRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return '';
  };

  if (loading && records.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading audit records...</span>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center text-gray-500">
          <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium">No audit records found</p>
          <p className="text-sm mt-1">Try adjusting your filters or date range</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Timestamp
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Event
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Item
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {records.map((record) => {
              const eventDisplay = getEventTypeDisplay(record.eventType);
              const actionColor = getActionColor(record.action);
              const relativeTime = getRelativeTime(record.timestamp);

              return (
                <tr
                  key={record.auditId}
                  onClick={() => onRowClick(record)}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  {/* Timestamp */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm text-gray-900">
                          {formatTimestamp(record.timestamp)}
                        </div>
                        {relativeTime && (
                          <div className="text-xs text-gray-500">{relativeTime}</div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Event Type */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${eventDisplay.color}`}>
                      {eventDisplay.label}
                    </span>
                  </td>

                  {/* Item */}
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <Package className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                          {record.targetName}
                        </div>
                        {record.targetSerial && (
                          <div className="text-xs text-gray-500">
                            SN: {record.targetSerial}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* User */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <div>
                        <div className="text-sm text-gray-900">{record.actorEmail}</div>
                        <div className="text-xs text-gray-500">{record.actorRole}</div>
                      </div>
                    </div>
                  </td>

                  {/* Description */}
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-700 max-w-[300px] truncate">
                      {record.description}
                    </div>
                    {record.changedFields && record.changedFields.length > 0 && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        Changed: {record.changedFields.slice(0, 3).join(', ')}
                        {record.changedFields.length > 3 && ` +${record.changedFields.length - 3} more`}
                      </div>
                    )}
                  </td>

                  {/* Details Arrow */}
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="h-5 w-5 text-gray-400 inline" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AuditTable;
